// Process-wide cache for the ray-trace reflection cloud.
//
// Mirrors `volumetricCache` (see that file's header for the full rationale).
// The position search browses many candidate placements and the editor flips
// between them; without a cache the ray trace is recomputed from scratch on
// every flip even when the user is revisiting a candidate they already saw.
//
// Unlike the volumetric cube, the ray trace DOES depend on the listener
// position (`listener_pos` is part of the request — the cloud's energies are
// sampled at the seat), so the cache key is the full request payload.  That
// means a seat-only search produces one trace per candidate, each cached as
// the user browses, and flipping back to a visited candidate is instant.
//
// Entries are bounded by a small LRU so a long search can't grow memory
// without limit. Thin wrapper around the generic createFetchCache — see
// that file for the shared Map/inflight/eviction implementation.

import type { RaytraceResponse, RaytraceRequest } from './adminTypes';
import { createFetchCache } from './createFetchCache';

const cache = createFetchCache<string, RaytraceResponse>({ sig: (s) => s });

/** Trace-determining signature — the full request fully determines the
 *  output, so we key on it directly (listener included, unlike volumetric). */
export function raytraceSig(req: RaytraceRequest): string {
  return JSON.stringify(req);
}

/** Synchronous cache read; bumps the entry to most-recently-used. */
export function getCachedRaytrace(sig: string): RaytraceResponse | null {
  return cache.getCached(sig) ?? null;
}

/** Read-through fetch with in-flight dedup — `fetcher` runs the network
 *  request only on a cold miss; concurrent callers for the same signature
 *  share the one promise. */
export async function fetchRaytrace(
  sig: string,
  fetcher: () => Promise<RaytraceResponse>,
): Promise<RaytraceResponse> {
  return cache.fetch(sig, fetcher);
}

/** Drop everything — use on doc reset / sign-out. */
export function clearRaytraceCache(): void {
  cache.clear();
}

/** Land a precomputed trace so the next `fetchRaytrace` for this sig is a
 *  hit. Signature must be `raytraceSig(docToRaytraceRequest(...))` — same
 *  request the live fetch will build (see applyTemplateDetail). */
export function primeCachedRaytrace(sig: string, resp: RaytraceResponse): void {
  cache.prime(sig, resp);
}
